/**
 * The `#tag` menu itself — the popover, where it is drawn and which row is lit.
 *
 * Extracted from [TagInputField] when a second field wanted the same menu without the same
 * field. There are two of them now and they disagree about almost everything: one turns a
 * committed tag into a chip and holds the set outside itself ([TagInputField]), the other
 * *is* the list, as text somebody can rearrange ([TagListField]). What they agree about is
 * this — a list of rows under the caret, arrow keys, and a click that commits.
 *
 * It owns no rules. Which rows exist is [tagMenu]'s answer and what a key does is
 * [tagKeyOutcome]'s; this draws what it is given and reports which row was pressed. That is
 * why it can be shared by two fields whose rules differ: neither of them is in here.
 */
package se.soderbjorn.lunapin.web

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLUListElement

/**
 * A menu anchored to [anchor].
 *
 * @param anchor the input it is drawn under — read for its rectangle on every placement,
 *   never written to.
 * @param onChoose a row was pressed. The index into the rows last passed to [open].
 */
internal class TagMenuPopover(
    private val anchor: HTMLInputElement,
    private val onChoose: (Int) -> Unit,
) {
    /**
     * The popover.
     *
     * **Positioned `fixed` and placed from the input's own rectangle**, rather than
     * absolutely inside the field. That is not a preference — an absolutely-positioned menu
     * is clipped by the first scrolling ancestor, and every surface that wants one of these
     * has one: the edit window's left column scrolls, the list window's rows scroll, the
     * settings window's pane scrolls, and the toolkit's pane body is `overflow: hidden`
     * outside all of them. A menu drawn inside those is a menu with two rows visible and the
     * rest cut off, which is the same as no menu.
     *
     * `fixed` escapes all of that because nothing between here and the document root
     * establishes a containing block for it — no ancestor carries a `transform`, `filter`
     * or `contain`, which are the three things that would make "fixed" mean "fixed to that
     * element" instead. If one ever does, this is the line that breaks, and it breaks
     * visibly.
     */
    val element: HTMLUListElement = (document.createElement("ul") as HTMLUListElement).apply {
        className = "lp-tagfield-menu"
        setAttribute("role", "listbox")
        hidden = true
    }

    /** The rows on screen, in order. Empty means closed. */
    var rows: List<TagMenuRow> = emptyList()
        private set

    /**
     * Which row is highlighted, or -1.
     *
     * Reset to 0 on every [open], which is the rule that the first match is highlighted by
     * default: the commonest case is typing enough letters that the top row is the intended
     * tag, and `⏎` should take it without an arrow key first.
     */
    var highlighted: Int = -1
        private set

    val isOpen: Boolean get() = !element.hidden

    init {
        // A `fixed` popover does not travel with what it is anchored to, so anything that
        // moves the input has to move it too. Cheaper and steadier than following: put it
        // back where the input now is. Scroll is watched in the capture phase because the
        // scrolling element is somewhere above this control and `scroll` does not bubble —
        // and a scroll of the menu's *own* rows is ignored, or reading a long list would
        // shuffle it under the caret on every wheel tick.
        window.addEventListener("resize", { if (isOpen) place() })
        document.addEventListener(
            "scroll",
            { event -> if (isOpen && event.target != element) place() },
            true,
        )
    }

    /**
     * Draw [next], with the first row lit. Closes instead when there is nothing to show.
     *
     * **Re-opening on the same rows is a no-op**, and that is two things rather than one.
     * A host repaints for reasons that have nothing to do with this menu — a list window
     * repaints on every keystroke, and each repaint hands the field its tags again — so
     * without this the popover's DOM was wiped and rebuilt several times per character. It
     * also **kept the highlight**: [highlighted] is reset to the first row on every open, so
     * an arrow key pressed on a menu the host then re-opened was silently undone.
     */
    fun open(next: List<TagMenuRow>) {
        if (next.isEmpty()) {
            close()
            return
        }
        if (isOpen && rows == next) return
        rows = next
        highlighted = 0
        render()
        element.hidden = false
        place()
        anchor.setAttribute("aria-expanded", "true")
    }

    fun close() {
        rows = emptyList()
        highlighted = -1
        element.hidden = true
        element.innerHTML = ""
        anchor.setAttribute("aria-expanded", "false")
    }

    /** Light row [to] instead. */
    fun moveHighlight(to: Int) {
        highlighted = to
        render()
    }

    /**
     * Put the popover under the input — or over it, when there is no room below.
     *
     * Measured on every open and every repaint rather than once, because the input moves: a
     * window is dragged, a column scrolls, a chip wraps onto a second line and pushes the
     * field down. Reading the rectangle is what makes those free instead of each being a
     * case to handle.
     *
     * Flipping above is not a nicety either. This control's commonest home is the bottom
     * half of a short window, which is exactly where a menu drawn downwards has nowhere to
     * go.
     */
    private fun place() {
        val rect = anchor.getBoundingClientRect()
        val below = window.innerHeight - rect.bottom
        // Measured after the rows are in, so this is the height it will actually be.
        val height = element.offsetHeight
        val style = element.style
        style.left = "${rect.left}px"
        style.width = "${rect.width}px"
        if (below < height + MENU_GAP_PX && rect.top > below) {
            style.top = "${rect.top - height - MENU_GAP_PX}px"
        } else {
            style.top = "${rect.bottom + MENU_GAP_PX}px"
        }
    }

    private fun render() {
        element.innerHTML = ""
        rows.forEachIndexed { index, row ->
            val item = element(
                "li",
                buildString {
                    append(
                        if (row is TagMenuRow.Create) {
                            "lp-tagfield-row lp-tagfield-row-create"
                        } else {
                            "lp-tagfield-row"
                        },
                    )
                    if (index == highlighted) append(" lp-tagfield-row-on")
                },
            )
            item.setAttribute("role", "option")
            item.setAttribute("aria-selected", (index == highlighted).toString())
            when (row) {
                is TagMenuRow.Existing -> {
                    item.appendChild(element("span", "lp-tagfield-row-name", row.use.tag))
                    if (row.use.fromAi) {
                        // One small mark, never a second hue and never a dashed
                        // "suggestion": an AI tag is applied and filed like any other, and
                        // this only says who put it there.
                        item.appendChild(element("span", "lp-tagfield-row-ai", "AI"))
                    }
                    item.appendChild(element("span", "lp-tagfield-row-uses", row.use.uses.toString()))
                }

                is TagMenuRow.Create -> {
                    item.appendChild(element("span", "lp-tagfield-row-plus", "+"))
                    item.appendChild(element("span", "lp-tagfield-row-name", "Create ${row.tag}"))
                    item.appendChild(element("span", "lp-tagfield-row-uses", "NEW"))
                }
            }
            // `mousedown`, not `click`: the input's blur fires first on a click and would
            // have closed the menu before the row it was aimed at could act.
            item.addEventListener("mousedown", { event ->
                event.preventDefault()
                onChoose(index)
            })
            element.appendChild(item)
        }
    }

    private companion object {
        /** The gap between the input and its popover, in pixels. */
        const val MENU_GAP_PX = 4
    }
}
